import { useState, useEffect } from 'react';
import axios from 'axios';
import { getCernerEmbeddedBcsToken, isCCF } from '../utils/third_party/cerner';
import { getResolvedSearchLocation } from '../utils/location';
import { fetchSessionInfo } from '../direct-booking/api/smart';

/**
 * @typedef {Object} DemographicsLocation
 * @property {string | undefined} patientZipSearchLocation - geo-resolved location (lat/lng or zip)
 * @property {string | undefined} patientZip - patient zipcode if one was fetched
 * @property {boolean} patientZipPending - if demographics are in the loading state
 * @property {string | undefined} patientZipError - an error, if any, raised when getting demographics
 */

/**
 * Unified hook that handles both CCF and SMART on FHIR demographics location
 * based on the customer configuration. This hook ensures only the appropriate
 * integration makes API calls, avoiding unnecessary requests.
 *
 * @param {Object} params - The hook parameters
 * @param {Object} params.config - customer Config from redux
 * @returns {DemographicsLocation}
 */
export default function useDemographicsLocation({ config }) {
  const [fetchedZip, setFetchedZip] = useState(undefined);
  const [searchLocation, setSearchLocation] = useState(undefined);
  const [error, setError] = useState(undefined);

  // Determine which integration is enabled
  const isSmartOnFhirEnabled = Boolean(config?.third_party?.cerner?.iss);
  const isCcfEnabled =
    isCCF() &&
    Boolean(config?.third_party?.cerner_healthelife?.search_auto_radius);

  // Only set pending if one of the integrations is enabled
  const [isPending, setIsPending] = useState(
    isSmartOnFhirEnabled || isCcfEnabled
  );

  useEffect(() => {
    // Early return if no integration is enabled
    if (!isSmartOnFhirEnabled && !isCcfEnabled) {
      setIsPending(false);
      return;
    }

    async function fetchDemographics() {
      try {
        setIsPending(true);

        let demographicsResponse;

        if (isSmartOnFhirEnabled) {
          // SMART on FHIR integration
          const smartSessionInfo = await fetchSessionInfo();

          if (!smartSessionInfo?.patientId) {
            setIsPending(false);
            return;
          }

          demographicsResponse = await fetchSmartPatientDemographics({
            patientId: smartSessionInfo.patientId,
            accessToken: smartSessionInfo.accessToken,
            serverUrl: smartSessionInfo.serverUrl
          });
        } else if (isCcfEnabled) {
          // Legacy CCF integration using BCS token
          const bcsToken = await getCernerEmbeddedBcsToken();

          if (!bcsToken) {
            setIsPending(false);
            return;
          }

          demographicsResponse = await fetchCcfPatientInformation({
            bcsToken
          });
        }

        if (!demographicsResponse?.result?.address?.zip) {
          setIsPending(false);
          return;
        }

        let zip = demographicsResponse.result.address.zip;

        if (typeof zip === 'string' && zip.length > 5) {
          zip = zip.substring(0, 5);
        }

        let searchLocation = zip;

        try {
          searchLocation = await getResolvedSearchLocation(zip);
        } catch (e) {
          // ignore, we'll just use `zip` as the location
        }

        setSearchLocation(searchLocation);
        setFetchedZip(zip);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsPending(false);
      }
    }

    fetchDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartOnFhirEnabled, isCcfEnabled]);

  return {
    patientZipSearchLocation: searchLocation,
    patientZip: fetchedZip,
    patientZipPending: isPending,
    patientZipError: error
  };
}

async function fetchCcfPatientInformation({ bcsToken }) {
  const response = await axios.post(`/api/patient-lookup/ccf/patient`, {
    bcsToken
  });
  return response.data;
}

// SMART on FHIR integration - make FHIR call directly from client
async function fetchSmartPatientDemographics({
  patientId,
  accessToken,
  serverUrl
}) {
  // Make FHIR API call directly from the client
  const response = await axios.get(`${serverUrl}/Patient/${patientId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/fhir+json'
    }
  });

  const patient = response.data;

  // Extract address from FHIR Patient resource
  const addresses = patient.address || [];
  const homeAddress =
    addresses.find((addr) => addr.use === 'home') || addresses[0];

  if (!homeAddress) {
    throw new Error('No address found in patient demographics');
  }

  // Transform to match the expected response format
  return {
    result: {
      address: {
        zip: homeAddress.postalCode
      }
    }
  };
}
